/**
 * Iter 45g + FP3-028B — Shared provider-workspace student fetch.
 *
 * Every provider-facing surface (Caseload, Goal Builder, Schedule
 * builder, Live Data Collection, etc.) needs the SAME thing: the
 * current provider's students, scoped server-side via `provider_id`.
 *
 * Before this hook each surface called `api.getStudents({})` with
 * different ad-hoc scoping — or none. The result was Sarah's Caseload
 * silently displaying 18 students when only 13 were hers, because
 * unscoped `/api/students` returns the whole school.
 *
 * FP3-028B — Role/identity now comes from the AUTHENTICATED user's
 * linked provider profile (`useAuth().provider`), NOT from the legacy
 * `is_current=True` provider row. This way Riley logging in shows
 * Riley's caseload + Riley's role-aware Goal Area taxonomy — never
 * Sarah's.
 *
 * Logic (single source of truth):
 *   1. Wait for auth context to finish loading.
 *   2. If the logged-in user has a linked provider — use it.
 *   3. Otherwise (e.g. admin without their own provider profile) —
 *      fall back to `/providers/current` so admin act-as surfaces
 *      still resolve a coherent provider context.
 *
 * Returns:
 *   provider   — the current provider record (null while loading)
 *   students   — array of students assigned to that provider
 *   loading    — true until BOTH provider and students have resolved
 *   error      — last error, if any
 *   refetch    — re-runs the students fetch (e.g. after archive)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function useCurrentProviderStudents(extraParams = {}) {
  const { provider: authProvider, user, loading: authLoading } = useAuth();
  const [provider, setProvider] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stable JSON snapshot of extraParams so callers can pass an object
  // literal without retriggering on every render.
  const paramsKey = JSON.stringify(extraParams || {});
  const lastParams = useRef(paramsKey);

  const fetchStudents = useCallback(
    async (pid, params) => {
      if (!pid) {
        setStudents([]);
        setLoading(false);
        return;
      }
      try {
        const merged = { provider_id: pid, ...(params || {}) };
        const res = await api.getStudents(merged);
        setStudents(Array.isArray(res.data) ? res.data : []);
        setError(null);
      } catch (e) {
        setError(e);
        setStudents([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // FP3-028B — Resolve the active provider from auth. Wait for auth
  // to finish loading so we never briefly fall through to a stale
  // `getCurrentProvider()` call that returns Sarah and bleeds her
  // SLP role into the Goal Area dropdown.
  useEffect(() => {
    if (authLoading) {
      // Still loading auth — keep provider null and don't fetch.
      return;
    }

    let cancelled = false;

    if (authProvider && authProvider.id) {
      // Logged-in user has a linked provider — single source of truth.
      setProvider(authProvider);
      return () => {
        cancelled = true;
      };
    }

    if (!user) {
      // No session AND no linked provider. Surface null (the caller
      // already redirected to /login via ProtectedRoute).
      setProvider(null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    // Logged-in user without a linked provider (admin acting-as).
    // Fall back to the legacy `is_current=True` pointer so admin
    // can still inspect a provider workspace. The sidebar surfaces
    // a "Viewing as ..." chip in this case (see FP3-028 sidebar).
    api
      .getCurrentProvider()
      .then((r) => {
        if (cancelled) return;
        setProvider(r.data || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e);
        setProvider(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, authProvider, user]);

  // Re-fetch students whenever provider id or extra params change.
  useEffect(() => {
    setLoading(true);
    fetchStudents(provider?.id, JSON.parse(paramsKey));
    lastParams.current = paramsKey;
  }, [provider?.id, paramsKey, fetchStudents]);

  const refetch = useCallback(() => {
    setLoading(true);
    return fetchStudents(provider?.id, JSON.parse(lastParams.current));
  }, [provider?.id, fetchStudents]);

  return { provider, students, loading, error, refetch };
}
